/**
 * Day 84.6 P1.4 — Inline field-level error renderer.
 *
 * Filters `userErrors[]` by exact `field_path` match; renders one
 * <div role="alert"> per matching entry below an input. Form surfaces compose
 * <FieldError fieldPath="<dot.notation.path>" userErrors={state} /> beneath
 * each input that may receive validation errors.
 *
 * Exact-match semantic only V1 — no glob / prefix matching per L6 lock.
 * Multi-match renders one line per matching userError (handles edge case
 * where the same field receives multiple validation errors from a single
 * RPC call).
 *
 * UI_STANDARDS-compliant: red-600 / xs / mt-1 / role=alert. e2e harness hook
 * via data-testid per scripts/e2e-flows.cjs convention.
 *
 * Cross-references: errorCodes.ts (UserError type contract), Decision 45 ADR
 * (envelope shape + namespace), COMPONENT_STANDARDS §21 (canonical pattern).
 */

import type { JSX } from 'react';
import type { UserError } from '@/lib/errorCodes';

interface FieldErrorProps {
  fieldPath: string;
  userErrors: UserError[];
}

export function FieldError({ fieldPath, userErrors }: FieldErrorProps): JSX.Element | null {
  const matching = userErrors.filter((e) => e.field_path === fieldPath);
  if (matching.length === 0) return null;
  return (
    <div
      role="alert"
      className="text-xs text-red-600 mt-1"
      data-testid={`field-error-${fieldPath}`}
    >
      {matching.map((e, i) => (
        <div key={i}>{e.message}</div>
      ))}
    </div>
  );
}
